import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export function StillBeingHeld() {
  const [prayers, setPrayers] = useState([]);
  useEffect(() => { api.get("/still-being-held").then(({ data }) => setPrayers(data.prayers)); }, []);
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-20">
      <p className="mono-label mb-4">Still Being Held</p>
      <h1 className="font-heading text-4xl md:text-5xl text-ink leading-none">Still Being Held</h1>
      <p className="font-body text-lg text-ink-secondary mt-6 leading-relaxed">
        Some prayers remain with us for a long time. Their presence here is not a sign of failure or of insufficient faith — only that the circle has not forgotten.
      </p>
      <div className="mt-14 space-y-px bg-ink/15 border border-ink/15">
        {prayers.map((p) => (
          <article key={p.id} className="bg-paper p-8" data-testid={`still-${p.id}`}>
            <div className="flex items-baseline justify-between mb-3">
              <span className="mono-label">{p.category}{p.location ? ` · ${p.location}` : ""}</span>
              <span className="stamp">Still being held</span>
            </div>
            <h3 className="font-heading text-2xl text-ink">{p.display_name}</h3>
            <p className="font-body text-ink-secondary mt-2 leading-relaxed">{p.excerpt}</p>
          </article>
        ))}
        {prayers.length === 0 && <div className="bg-paper p-12 text-center font-body text-ink-tertiary italic">Nothing here for now.</div>}
      </div>
    </div>
  );
}

export function WordsForTheWay() {
  const [quotes, setQuotes] = useState([]);
  useEffect(() => { api.get("/quotes/public").then(({ data }) => setQuotes(data.quotes)); }, []);
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-20">
      <p className="mono-label mb-4">Words for the Way</p>
      <h1 className="font-heading text-4xl md:text-5xl text-ink leading-none">Words for the Way</h1>
      <p className="font-body text-lg text-ink-secondary mt-6 leading-relaxed max-w-2xl">
        Carefully sourced words that point toward attention, love, awakening, service and truth. Every quotation is verified before it appears here — we never fabricate or misattribute.
      </p>
      <div className="mt-16 space-y-16">
        {quotes.map((q) => (
          <blockquote key={q.id} className="border-l-2 border-terracotta pl-6" data-testid={`quote-${q.id}`}>
            <p className="font-heading italic text-2xl md:text-3xl text-ink leading-snug">“{q.quotation}”</p>
            <footer className="mt-5">
              <div className="font-body text-lg text-ink">{q.author}</div>
              <div className="mono-label mt-2">
                {[q.work, q.page && `p. ${q.page}`, q.tradition].filter(Boolean).join(" · ")}
              </div>
            </footer>
          </blockquote>
        ))}
        {quotes.length === 0 && <p className="font-body text-ink-tertiary italic">No words published yet.</p>}
      </div>
    </div>
  );
}

const principles = [
  "We hold one another in prayer.",
  "We do not prescribe the answer.",
  "We do not judge the worthiness of another person's prayer.",
  "We do not promise outcomes.",
  "We do not tell people that an unresolved prayer reflects insufficient faith.",
  "We respect silence, mystery and different understandings of prayer.",
  "We remain attentive to opportunities to serve.",
  "We remember that today I may help carry your prayer, and tomorrow I may need you to carry mine.",
  "We believe prayer can lead to service.",
];

export function OurPractice() {
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-20">
      <p className="mono-label mb-4">Our Practice</p>
      <h1 className="font-heading text-4xl md:text-5xl text-ink leading-none">Our Practice</h1>
      <div className="mt-14 space-y-8">
        {principles.map((p, i) => (
          <div key={i} className="flex gap-6 items-baseline" data-testid={`principle-${i}`}>
            <span className="mono-label">{String(i + 1).padStart(2, "0")}</span>
            <p className="font-heading text-2xl md:text-3xl text-ink leading-snug">{p}</p>
          </div>
        ))}
      </div>
      <p className="font-heading italic text-2xl md:text-3xl text-terracotta mt-16 paper-rule pt-12 leading-snug">
        May my life today become an answer to someone's prayer.
      </p>
    </div>
  );
}

export function About() {
  const paras = [
    "Human beings have always called into the mystery — in hope, grief, gratitude, desperation and love.",
    "We do not claim to know how prayer works.",
    "We do not claim to know why some prayers appear to be answered while others remain painfully unresolved.",
    "We choose instead to practice attention.",
    "We listen to one another's prayers.",
    "We hold them faithfully.",
    "We remain awake to what is given.",
    "And where love asks something of us, we try, when we can, to respond.",
  ];
  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 py-20">
      <p className="mono-label mb-4">About</p>
      <h1 className="font-heading text-4xl md:text-5xl text-ink leading-none">A quiet founding</h1>
      <div className="mt-12 space-y-6">
        {paras.map((p, i) => (
          <p key={i} className="font-body text-xl text-ink-secondary leading-relaxed">{p}</p>
        ))}
      </div>
      <p className="font-heading italic text-2xl md:text-3xl text-terracotta mt-16 leading-snug">
        May my life today become an answer to someone's prayer.
      </p>
    </div>
  );
}

const practiceArticles = [
  "How to Create a Simple Home Altar", "A Seven-Day Practice of Gratitude", "How to Keep a Prayer List",
  "What Does It Mean to Hold Someone in Prayer?", "Beginning and Ending the Day", "Lighting a Candle for Someone",
  "A Practice of Forgiveness", "When a Prayer Seems Unanswered", "Praying When You Don't Know What You Believe",
  "Creating a Place of Stillness at Home", "The Practice of Remembering", "How to Sit With Someone Without Trying to Fix Them",
  "Writing a Prayer by Hand", "Rituals for Beginnings and Endings", "A Practice for Grief",
  "Giving Thanks Before a Meal", "Making a Pilgrimage", "Keeping a Book of Answered Prayers",
];
const disciplines = [
  ["Prayer", "Learning to ask, listen, surrender and hold another person."],
  ["Gratitude", "Learning to recognise what has already been given — gratitude that can coexist with grief and uncertainty."],
  ["Forgiveness", "The difficult practice of releasing our demand that the past become different."],
  ["Service", "Making ourselves available to become part of an answer."],
  ["Ritual", "Giving physical form to attention, remembrance and transition."],
];

export function Practices() {
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-20">
      <p className="mono-label mb-4">Practices</p>
      <h1 className="font-heading text-4xl md:text-5xl text-ink leading-none">Practices</h1>
      <p className="font-body text-lg text-ink-secondary mt-6 leading-relaxed">
        Not a blog — thoughtful pieces you might return to for years, organised around five disciplines. None requires membership in any particular religion.
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-ink/15 border border-ink/15 mt-12">
        {disciplines.map(([t, d]) => (
          <div key={t} className="bg-paper p-6">
            <h3 className="font-heading text-2xl text-ink">{t}</h3>
            <p className="font-body text-sm text-ink-secondary mt-2 leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
      <div className="mt-16">
        <p className="mono-label mb-8">Coming to the library</p>
        <div className="space-y-px bg-ink/15 border border-ink/15">
          {practiceArticles.map((a, i) => (
            <div key={a} className="bg-paper p-5 flex items-baseline gap-5" data-testid={`practice-article-${i}`}>
              <span className="mono-label">{String(i + 1).padStart(2, "0")}</span>
              <span className="font-heading text-xl text-ink">{a}</span>
            </div>
          ))}
        </div>
        <p className="font-body text-ink-tertiary italic mt-8">These essays are being written with care and will appear here in time.</p>
      </div>
    </div>
  );
}

export function Policies() {
  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 py-20">
      <p className="mono-label mb-4">Placeholders for legal review</p>
      <h1 className="font-heading text-4xl md:text-5xl text-ink leading-none">Terms, Privacy & Guidelines</h1>
      <div className="mt-12 space-y-10 font-body text-lg text-ink-secondary leading-relaxed">
        <section>
          <h2 className="font-heading text-2xl text-ink mb-3">Community Guidelines</h2>
          <p>Do not share information that identifies another person without their permission. Do not use another's prayer to argue, advise or judge. Prayers are held, not debated. Grief, illness and suffering are treated with dignity.</p>
        </section>
        <section>
          <h2 className="font-heading text-2xl text-ink mb-3">Privacy</h2>
          <p>Prayers are treated as sensitive personal information. Email addresses and exact locations are never shown publicly. Nothing is published without moderation. You may request deletion of your account and prayers at any time.</p>
        </section>
        <section>
          <h2 className="font-heading text-2xl text-ink mb-3">Not an emergency service</h2>
          <p>The Ministry is not an emergency, medical or crisis service. If you or someone you know is in danger, please contact local emergency services or a crisis line immediately.</p>
        </section>
        <p className="mono-label">These are placeholders pending legal review.</p>
      </div>
    </div>
  );
}
